import { useState, useEffect, Fragment } from 'react';
import { toast } from 'react-toastify';
import { useConfirm } from 'material-ui-confirm';
import { v4 as uuidv4 } from 'uuid';

import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  Tooltip,
  Chip,
  Stack,
} from '@mui/material';

// Icons
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import AddIcon from '@mui/icons-material/Add';

// Custom Components
import { Spinner, SuppliersDrawer } from '../..';

// Utils
import messages from '../../../static/messages';

// Services
import suppliersServices from '../../../services/suppliersServices';

// Hooks
import useDrawer from '../../../hooks/useDrawer';

// Styles
import { table_header_with_search, table_container_base, actions_column } from '../../../static/styles';

import { button } from '../../../static/theme-styles';

// External variables
const base_supplier = {
  name: '',
  emails: [],
};

// Migrate old supplier format (email string) to new format (emails array)
const migrateSupplier = (supplier) => {
  // If already has emails array, return as is
  if (supplier.emails && Array.isArray(supplier.emails) && supplier.emails.length > 0) {
    return supplier;
  }
  
  // Migrate from old 'email' field
  if (supplier.email && typeof supplier.email === 'string') {
    return {
      ...supplier,
      emails: [supplier.email],
      email: undefined, // Remove old field
    };
  }
  
  // No email data, initialize empty array
  return {
    ...supplier,
    emails: [],
  };
};

const SuppliersTable = () => {
  const [suppliers, setSuppliers] = useState();
  const [currentSupplier, setCurrentSupplier] = useState(null);
  const [isNewSupplier, setSupplierMode] = useState(false);
  const [supplierIndex, setSupplierIndex] = useState(null);

  const { drawer, openDrawer, foolCheck } = useDrawer(currentSupplier);

  const confirm = useConfirm();

  // Action functions
  const onCloseDrawer = async (resetErrors) => {
    const confirmed = await foolCheck();

    if (confirmed === true) {
      setCurrentSupplier(null);
      setSupplierIndex(null);
      setSupplierMode(false);
      resetErrors && resetErrors();
    }
  };

  const onAddSupplier = () => {
    setSupplierMode(true);
    setCurrentSupplier({ ...base_supplier });
    openDrawer();
  };

  const onEditSupplier = (supplier, index) => {
    setCurrentSupplier({ ...supplier });
    setSupplierIndex(index);
    openDrawer();
  };

  // Handler functions
  const handleDelete = (index) => {
    confirm({
      title: messages.confirm_message.restore,
      description: messages.confirm_message.remove_description('supplier'),
    })
      .then(() => removeSupplier(index))
      .catch(() => toast.warning(messages.cancel_message));
  };

  // Async functions
  const getSuppliersList = async () => {
    try {
      const response = await suppliersServices.getSuppliers();
      let suppliersData = response.data.value.data || [];
      
      // Migration: Add id and migrate email format
      let needsUpdate = false;
      suppliersData = suppliersData.map(supplier => {
        let updated = { ...supplier };
        
        // Add id if missing
        if (!updated.id) {
          needsUpdate = true;
          updated.id = uuidv4();
        }
        
        // Migrate email to emails
        const migrated = migrateSupplier(updated);
        if (migrated.emails !== updated.emails || migrated.email !== updated.email) {
          needsUpdate = true;
        }
        
        return migrated;
      });
      
      // Save updated suppliers if migration occurred
      if (needsUpdate && suppliersData.length > 0) {
        await suppliersServices.update(suppliersData);
      }

      setSuppliers(suppliersData);
    } catch (error) {
      toast.error(messages.errors.error_data_loaning);
      console.log(error);
    }
  };

  const createSupplier = async () => {
    delete currentSupplier.isEdited;
    
    // Generate unique ID for new supplier
    const newSupplier = {
      ...currentSupplier,
      id: uuidv4(),
      email: undefined, // Don't save legacy field
    };

    const data = [...suppliers, newSupplier];

    try {
      const response = await suppliersServices.update(data);

      if (response.status === 200) {
        onCloseDrawer();
        toast.success(messages.supplier.create);
        getSuppliersList();
      }
    } catch (error) {
      toast.error(messages.errors.error_try_again);
      console.log(error);
    }
  };

  const updateSupplier = async () => {
    delete currentSupplier.isEdited;

    // Preserve the original ID, remove legacy field
    const originalSupplier = suppliers[supplierIndex];
    const updatedSupplier = {
      ...currentSupplier,
      id: currentSupplier.id || originalSupplier?.id || uuidv4(),
      email: undefined, // Don't save legacy field
    };

    const updatedData = suppliers.map((s, idx) => 
      idx === supplierIndex ? updatedSupplier : s
    );

    try {
      const response = await suppliersServices.update(updatedData);

      if (response.status === 200) {
        onCloseDrawer();
        toast.success(messages.supplier.update);
        getSuppliersList();
      }
    } catch (error) {
      toast.error(messages.errors.error_try_again);
      console.log(error);
    }
  };

  const removeSupplier = async (index) => {
    const updatedData = suppliers.filter((_, idx) => idx !== index);

    try {
      const response = await suppliersServices.update(updatedData);

      if (response.status === 200) {
        toast.success(messages.supplier.remove);
        getSuppliersList();
      }
    } catch (error) {
      toast.error(messages.errors.error_try_again);
      console.log(error);
    }
  };

  // Hooks
  useEffect(() => {
    getSuppliersList();
  }, []);

  // Check for data loading
  if (!suppliers) return <Spinner />;

  return (
    <Fragment>
      <Box sx={table_header_with_search}>
        <Button variant="contained" endIcon={<AddIcon />} onClick={onAddSupplier} sx={button('primary', 'secondary')}>
          Add new
        </Button>
      </Box>

      <Paper>
        <TableContainer sx={table_container_base}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell width="30%">Name</TableCell>
                <TableCell width="60%">Email Addresses</TableCell>
                <TableCell width="10%">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {suppliers.length ? (
                suppliers.map((supplier, index) => (
                  <TableRow key={supplier.id || index} hover>
                    <TableCell scope="row">{supplier.name}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {(supplier.emails || []).map((email, idx) => (
                          <Chip 
                            key={idx} 
                            label={email} 
                            size="small" 
                            variant="outlined"
                            sx={{ mb: 0.5 }}
                          />
                        ))}
                        {(!supplier.emails || supplier.emails.length === 0) && (
                          <span style={{ color: '#999', fontStyle: 'italic' }}>No emails</span>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell sx={actions_column}>
                      <Tooltip title="Edit Supplier">
                        <IconButton onClick={() => onEditSupplier(supplier, index)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Supplier">
                        <IconButton onClick={() => handleDelete(index)}>
                          <DeleteForeverIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={3} sx={{ textAlign: 'center' }}>
                    <strong>No Data</strong>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <SuppliersDrawer
        drawer={drawer}
        onCloseDrawer={onCloseDrawer}
        currentSupplier={currentSupplier}
        setCurrentSupplier={setCurrentSupplier}
        isNewSupplier={isNewSupplier}
        createSupplier={createSupplier}
        updateSupplier={updateSupplier}
      />
    </Fragment>
  );
};

export default SuppliersTable;
